import type { PhotoMissingReason, PunchPhotoStatus } from '@worklogix/shared';

/**
 * Pure sequencing + quota logic for the offline photo drain (plan-v7). Every
 * decision here operates on plain records — no IndexedDB, no network, no DOM — so
 * the ordering guarantees ("upload only AFTER the punch synced", "never re-upload
 * a synced photo", "drop the oldest already-uploaded photo on quota pressure")
 * are unit-testable in isolation. The IndexedDB store (`device/photoQueue.ts`)
 * and the network drain (`sync/syncEngine.ts`) are thin shells over these.
 *
 * ORDERING INVARIANT: a photo's upload targets `POST /kiosk/punch/:key/photo`,
 * whose FK requires the `kiosk_punch` row to already exist. So a photo is only
 * eligible once its punch reached the server (status `applied` | `duplicate`).
 * Uploading earlier races the punch and the server answers 409.
 */

/** A photo as seen by the pure drain logic (the blob itself is irrelevant here). */
export interface PhotoRecordView {
  idempotencyKey: string;
  status: PunchPhotoStatus;
  missingReason?: PhotoMissingReason;
  /** True once the server acknowledged the upload; blocks any re-send. */
  photoSynced: boolean;
  /** Client capture time (ISO-8601); drives oldest-first quota eviction. */
  capturedAt: string;
  /** Whether an encoded JPEG is still retained on-device. */
  hasBlob: boolean;
}

/**
 * The set of punch idempotency-keys whose punch has reached the server (its
 * `kiosk_punch.id` exists, so the photo FK is satisfiable).
 */
export type SyncedPunchKeys = ReadonlySet<string>;

/**
 * Photos eligible to upload RIGHT NOW, oldest-capture first:
 *   - not already `photoSynced` (idempotent: a synced photo is never re-sent);
 *   - AND the punch it belongs to has synced (key present in {@link synced}).
 * A photo whose punch has not yet synced is deliberately withheld until a later
 * drain — the server would only answer 409.
 */
export function selectUploadablePhotos(
  photos: readonly PhotoRecordView[],
  synced: SyncedPunchKeys
): PhotoRecordView[] {
  return photos.filter((p) => !p.photoSynced && synced.has(p.idempotencyKey)).sort(byCapturedAtAsc);
}

/** Count of photos still awaiting upload (not yet `photoSynced`). */
export function countUnsyncedPhotos(photos: readonly PhotoRecordView[]): number {
  return photos.reduce((n, p) => (p.photoSynced ? n : n + 1), 0);
}

/**
 * On `QuotaExceededError`, choose the photo to evict to make room: the OLDEST
 * photo that is ALREADY uploaded (`photoSynced === true`) — dropping it loses
 * nothing (the server has it), while an un-uploaded photo is never sacrificed.
 * Returns the idempotency-key to delete, or null when nothing is safely
 * evictable (caller then keeps the punch, drops only the incoming blob + warns).
 */
export function selectEvictablePhoto(photos: readonly PhotoRecordView[]): string | null {
  const evictable = photos.filter((p) => p.photoSynced).sort(byCapturedAtAsc);
  return evictable.length > 0 ? (evictable[0] as PhotoRecordView).idempotencyKey : null;
}

/** The result of a single upload attempt, from the drain's point of view. */
export type PhotoUploadOutcome =
  /** Server accepted (2xx / already_stored): mark synced, drop the blob. */
  | 'uploaded'
  /** Server 409: the punch has not synced yet — retry after the next punch drain. */
  | 'punch_not_synced'
  /** Transient transport/5xx failure — bump attempts, retry with backoff. */
  | 'transient';

/**
 * Fold a single upload {@link PhotoUploadOutcome} onto a photo record, returning
 * the NEXT record state. Pure, so the drain reducer can be tested end-to-end
 * (proving an offline batch drain reaches the same end-state as one-at-a-time
 * online uploads).
 */
export function applyUploadOutcome(
  photo: PhotoRecordView,
  outcome: PhotoUploadOutcome
): PhotoRecordView {
  switch (outcome) {
    case 'uploaded':
      return { ...photo, photoSynced: true, hasBlob: false };
    case 'punch_not_synced':
    case 'transient':
      return photo;
    default:
      return photo;
  }
}

function byCapturedAtAsc(a: PhotoRecordView, b: PhotoRecordView): number {
  if (a.capturedAt !== b.capturedAt) {
    return a.capturedAt < b.capturedAt ? -1 : 1;
  }
  return a.idempotencyKey < b.idempotencyKey ? -1 : a.idempotencyKey > b.idempotencyKey ? 1 : 0;
}
